import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { getCheckinBusinessBillingTableAction } from "../../../redux/actions";
import { setSelectedBusiness } from "../../../redux/slices/AdminSlices/GBPSlice";
import MaterialIcons from "../../../assests/MaterialIcons/MaterialIcons";
import useDebounce from "../../../Hooks/useDebounce";
import { menuItemSX } from "../../Builder/BuilderConst";
import { commonMenuProps } from "../../../theme/CommonStyles";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../utils/routes";
import "../../Modals/ConfirmationMsgModal/ConfirmationMsgModal.scss";
import "./LocationsModal.scss";

interface LocationsModalProps {
  open: boolean;
  handleClose: () => void;
  initialStatusFilter?: string;
}

const LocationsModal: React.FC<LocationsModalProps> = ({
  open,
  handleClose,
  initialStatusFilter = "",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    checkinBillingTable,
    checkinBillingTablePagination,
    checkinBillingTableLoading,
  } = useSelector((state: RootState) => state.billing);

  const handleViewLocation = (item: any) => {
    handleClose();
    if (item?._id) {
      dispatch(setSelectedBusiness(item._id));
      localStorage.setItem("selected_business", item._id);
    }
    navigate(routes?.mapCheckingSetting || "/mapcheckins-setting", {
      state: 4,
    });
  };

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(initialStatusFilter);
  const [prevOpen, setPrevOpen] = useState(open);

  if (open && !prevOpen) {
    setPrevOpen(true);
    setStatusFilter(initialStatusFilter || "");
    setPage(1);
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "",
  });

  const debouncedSearch = useDebounce(searchTerm, 500);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
    setPage(1);
  };

  const getSortArrow = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <MaterialIcons.ArrowDropUp
        className="arrow-up-animation"
        style={{ width: "20px" }}
      />
    ) : (
      <MaterialIcons.ArrowDropUp
        className="arrow-down-animation"
        style={{ width: "20px" }}
      />
    );
  };

  useEffect(() => {
    if (open) {
      const payload: any = {
        page,
        limit: rowsPerPage,
        keyword: debouncedSearch,
      };

      if (statusFilter) {
        payload.status = statusFilter;
      }

      if (sortConfig.key) {
        payload.sort_by = sortConfig.key;
        payload.sort_order = sortConfig.direction;
      }

      dispatch(getCheckinBusinessBillingTableAction(payload));
    }
  }, [
    open,
    page,
    rowsPerPage,
    debouncedSearch,
    statusFilter,
    sortConfig,
    dispatch,
  ]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const getStatusChip = (status?: string, trialDays?: number | null) => {
    const rawStatus = (status || "").toLowerCase().trim();
    let label = rawStatus
      ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)
      : "—";
    let className = "badge-status active";

    if (rawStatus === "trialing" || rawStatus === "trial") {
      label =
        trialDays !== null && trialDays !== undefined
          ? `Trial · ${trialDays} ${trialDays === 1 ? "day" : "days"}`
          : "Trialing";
      className = "badge-status trialing";
    } else if (rawStatus === "failed" || rawStatus === "unpaid") {
      label = "Failed";
      className = "badge-status failed";
    } else if (rawStatus === "paid") {
      label = "Paid";
      className = "badge-status paid";
    } else if (rawStatus === "active") {
      label = "Active";
      className = "badge-status active";
    } else if (rawStatus === "canceled" || rawStatus === "inactive") {
      label = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
      className = "badge-status canceled";
    }

    return <span className={className}>{label}</span>;
  };

  const safeBusinessList = Array.isArray(checkinBillingTable)
    ? checkinBillingTable
    : [];

  const totalItems =
    checkinBillingTablePagination?.totalItems ?? safeBusinessList.length;
  const totalPages =
    checkinBillingTablePagination?.totalPages ??
    (Math.ceil(totalItems / rowsPerPage) || 1);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: "var(--background-color)",
          maxWidth: "850px !important",
          width: "100% !important",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        className="confirm-modal-title"
        sx={{ pb: 1, pt: 2.5, px: 3 }}
      >
        <div
          className="confirm-title-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            // alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography className="locations-modal-title">
              Map Check-Ins locations
            </Typography>
            <Typography className="locations-modal-subtitle">
              Each location bills on its own cycle.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "10px",
            }}
          >
            <IconButton
              onClick={handleClose}
              className="close-btn"
              size="small"
              sx={{ p: 0.5, mt: "2px", mr: -0.5 }}
            >
              <CloseIcon sx={{ fontSize: "20px" }} />
            </IconButton>
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as string);
                setPage(1);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "All Status";
                if (selected === "active" || selected === "paid")
                  return "Active";
                if (selected === "trialing" || selected === "trial")
                  return "Trialing";
                if (selected === "failed") return "Failed";
                return selected;
              }}
              MenuProps={commonMenuProps}
              sx={{
                borderRadius: "var(--small-border-radius)",
                backgroundColor: "var(--background-color)",
                textTransform: "capitalize",
                fontSize: "var(--small-body-font-size)",
                fontFamily: "var(--font-family-manrope)",
                height: "34px",
                minWidth: "120px",
                bgcolor: statusFilter !== "" ? "#e3f2fd !important" : "#ffffff",
                color:
                  statusFilter !== ""
                    ? "#000000 !important"
                    : "#7F7F7F !important",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: statusFilter !== "" ? "#1A73E8" : "#d8d8d8",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1A73E8",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1A73E8",
                },
              }}
            >
              <MenuItem sx={menuItemSX} value="">
                <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  All Status
                </Box>
              </MenuItem>
              <MenuItem sx={menuItemSX} value="active">
                <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  Active
                </Box>
              </MenuItem>
              <MenuItem sx={menuItemSX} value="trialing">
                <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  Trialing
                </Box>
              </MenuItem>
              <MenuItem sx={menuItemSX} value="failed">
                <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  Failed
                </Box>
              </MenuItem>
            </Select>
          </Box>
        </div>
      </DialogTitle>

      <DialogContent
        className="confirm-modal-container locations-modal-container"
        sx={{
          flexDirection: "column",
          px: 3,
          pb: 3,
          pt: 1,
          overflowY: "auto",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <div
          className="custom-business-table-wrapper dash-table"
          style={{
            marginTop: 0,
            marginLeft: 0,
            marginBottom: 0,
            width: "100%",
            maxWidth: "100%",
            padding: 0,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              width: "100%",
              overflowX: "auto",
              maxHeight: "calc(90vh - 220px)",
              overflowY: "auto",
            }}
          >
            <table
              className="business-table"
              style={{ width: "100%", minWidth: "700px" }}
            >
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("name")}
                    style={{
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "var(--bg-table-th, #f8fafc)",
                    }}
                  >
                    Location {getSortArrow("name")}
                  </th>
                  <th
                    onClick={() => handleSort("price")}
                    style={{
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "var(--bg-table-th, #f8fafc)",
                    }}
                  >
                    Price {getSortArrow("price")}
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    style={{
                      cursor: "pointer",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "var(--bg-table-th, #f8fafc)",
                    }}
                  >
                    Status {getSortArrow("status")}
                  </th>
                  <th
                    onClick={() => handleSort("next_charge")}
                    style={{
                      cursor: "pointer",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "var(--bg-table-th, #f8fafc)",
                    }}
                  >
                    Next Charge {getSortArrow("next_charge")}
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "var(--bg-table-th, #f8fafc)",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {safeBusinessList.length > 0 ? (
                  safeBusinessList.map((item: any) => {
                    const isTrialing =
                      item?.status === "trialing" || item?.status === "trial";
                    const itemPrice =
                      item?.price !== undefined ? item.price : 299;

                    return (
                      <tr key={item._id || item.id}>
                        <td style={{ maxWidth: "280px" }}>
                          <div
                            className="business-name"
                            style={{
                              fontWeight: 600,
                              color: "#0f172a",
                              maxWidth: "260px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item?.business_name || "N/A"}
                          >
                            {item?.business_name || "N/A"}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              marginTop: "2px",
                              maxWidth: "260px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item?.address || "—"}
                          >
                            {item?.address || "—"}
                          </div>
                        </td>
                        <td>
                          {/* {isTrialing || itemPrice === 0 ? (
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#0f172a",
                              }}
                            >
                              Free{" "}
                              <span
                                style={{
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  color: "#64748b",
                                }}
                              >
                                during trial
                              </span>
                            </span>
                          ) : ( */}
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            ${itemPrice}
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#64748b",
                              }}
                            >
                              {" "}
                              /mo
                            </span>
                          </span>
                          {/* )} */}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {getStatusChip(
                            item?.status,
                            item?.trial_days_remaining,
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ fontSize: "14px", color: "#334155" }}>
                            {item?.next_charge
                              ? moment(item.next_charge).format("MMM DD, YYYY")
                              : item?.expiry
                                ? moment(item.expiry).format("MMM DD, YYYY")
                                : "—"}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className="view-link"
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#1A73E8",
                              fontFamily: "var(--font-family-manrope)",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() => handleViewLocation(item)}
                          >
                            View
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: "center",
                        padding: "30px",
                        color: "#6b7280",
                      }}
                    >
                      No locations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span>
              {totalItems > 0 ? (
                <>
                  {(page - 1) * rowsPerPage + 1}–
                  {Math.min(page * rowsPerPage, totalItems)} of {totalItems}{" "}
                  results shown
                </>
              ) : (
                "0–0 of 0 results shown"
              )}
            </span>

            <div className="page-controls">
              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                sx={{
                  "& .Mui-selected": {
                    color: "white !important",
                    backgroundColor:
                      "var(--action-button-background-color) !important",
                  },
                }}
              />
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                size="small"
                sx={{
                  overflow: "hidden",
                  "& .MuiInputBase-root": {
                    backgroundColor: "#e3f2fd !important",
                    borderRadius: "var(--small-border-radius)",
                    fontWeight: 400,
                    backgroundClip: "padding-box !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#e3f2fd !important",
                    borderRadius: "var(--small-border-radius)",
                    backgroundClip: "padding-box !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "var(--small-border-radius)",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      ...commonMenuProps.PaperProps.sx,
                      minWidth: 100,
                    },
                  },
                }}
              >
                {[5, 10, 20].map((val) => (
                  <MenuItem key={val} value={val} sx={menuItemSX}>
                    {val} / page
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationsModal;
